import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Sparkles, CheckCircle2, XCircle, Clock, Image as ImageIcon, ChevronUp, ChevronDown } from "lucide-react";
import { PageHeader } from "../components/shared/PageHeader";
import { DataTable } from "../components/shared/DataTable";
import { Pagination } from "../components/shared/Pagination";
import { StatusBadge } from "../components/shared/StatusBadge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { MultiStatusFilter } from "../components/shared/MultiStatusFilter";
import { SortControl } from "../components/shared/SortControl";
import { GenerateDialog } from "../components/deliveries/GenerateDialog";
import { PodViewerDialog } from "../components/deliveries/PodViewerDialog";
import { PausedTiffinsPanel } from "../components/deliveries/PausedTiffinsPanel";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useListState } from "../hooks/useListState";
import { formatDate, todayLocal as today } from "../lib/utils";
import { deliveryStatusMeta } from "../lib/statusMaps";

const PAGE_SIZE = 25;
const STATUS_OPTIONS = [
  { value: "1", label: "Pending" }, { value: "2", label: "Delivered" }, { value: "3", label: "Not Delivered" },
];
const SORT_FIELDS = [{ value: "date", label: "Date" }];

function SortableDateHeader({ label, sortDir, onSort }) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 hover:text-text-main"
      onClick={onSort}
      data-testid="delivery-header-sort-date"
    >
      {label}
      <span className="flex flex-col leading-none">
        <ChevronUp size={10} strokeWidth={2.5} className={sortDir === "asc" ? "text-primary" : "text-text-muted/40"} />
        <ChevronDown size={10} strokeWidth={2.5} className={sortDir === "desc" ? "text-primary" : "text-text-muted/40"} />
      </span>
    </button>
  );
}

function routeDriverLabel(r) {
  if (r.driver_name) return r.driver_name;
  if (r.route_id) return `Route #${r.route_id}`;
  return "Unassigned";
}

export default function Deliveries() {
  const [date, setDate] = useListState("deliveries.date", today());
  const [searchParams, setSearchParams] = useSearchParams();
  // Honor a ?date= param (e.g. the Live Map "Deliveries" button) once on arrival.
  useEffect(() => {
    const urlDate = searchParams.get("date");
    if (urlDate) {
      setDate(urlDate);
      searchParams.delete("date");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [search, setSearch] = useListState("deliveries.search", "");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [statusFilters, setStatusFilters] = useListState("deliveries.statusFilters", []);
  const [sortDir, setSortDir] = useListState("deliveries.sortDir", "desc");
  const [page, setPage] = useListState("deliveries.page", 0);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [podDelivery, setPodDelivery] = useState(null);
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: ["deliveries-list", { date, q: debouncedSearch, statusFilters: statusFilters.join(","), sortDir, page }],
    queryFn: async () => {
      const { data } = await apiClient.get("/deliveries", {
        params: {
          date: date || undefined,
          q: debouncedSearch || undefined,
          delivery_status: statusFilters.length ? statusFilters.join(",") : undefined,
          sort_dir: sortDir,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        },
      });
      return data;
    },
    placeholderData: keepPreviousData,
  });
  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const error = listQuery.isError ? getApiErrorMessage(listQuery.error, "Could not load deliveries.") : null;

  const filterKey = `${date}|${debouncedSearch}|${statusFilters.join(",")}|${sortDir}`;
  const prevFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey;
      if (page !== 0) setPage(0);
    }
  }, [filterKey, page, setPage]);

  // A status change updates the delivery row AND the route's done/total
  // counters + today's delivery count, so invalidate those views too.
  const statusMutation = useMutation({
    mutationFn: ({ delivery, status }) => apiClient.post(`/deliveries/${delivery.id}/status`, { status }),
    onSuccess: (_res, { delivery, status }) => {
      toast.success(`Delivery #${delivery.id} marked ${deliveryStatusMeta(status).label}`);
      queryClient.invalidateQueries({ queryKey: ["deliveries-list"] });
      queryClient.invalidateQueries({ queryKey: ["routes-list"] });
      queryClient.invalidateQueries({ queryKey: ["subscription-deliveries-today-count"] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, "Could not update delivery status.")),
  });
  const actingId = statusMutation.isPending ? statusMutation.variables?.delivery?.id : null;
  const setStatus = (delivery, status) => statusMutation.mutate({ delivery, status });

  const handleHeaderSort = () => setSortDir(sortDir === "asc" ? "desc" : "asc");

  const columns = [
    { key: "id", header: "ID", className: "text-text-muted" },
    {
      key: "date",
      header: <SortableDateHeader label="Date" sortDir={sortDir} onSort={handleHeaderSort} />,
      render: (r) => formatDate(r.date),
    },
    {
      key: "customer", header: "Customer", primary: true,
      render: (r) => (
        <Link to={`/customers/${r.customer_id}`} className="text-primary hover:underline" data-testid={`delivery-customer-link-${r.id}`}>
          {r.customer_name || `#${r.customer_id}`}
        </Link>
      ),
    },
    {
      key: "subscription", header: "Subscription",
      render: (r) => (
        <Link to={`/subscriptions/${r.subscription_id}`} className="text-primary hover:underline" data-testid={`delivery-subscription-link-${r.id}`}>
          #{r.subscription_id}
        </Link>
      ),
    },
    { key: "plan_name", header: "Plan", subtitle: true, render: (r) => r.plan_name || "—" },
    {
      key: "status", header: "Status",
      render: (r) => { const m = deliveryStatusMeta(r.delivery_status); return <StatusBadge label={m.label} variant={m.variant} />; },
    },
    { key: "route_driver", header: "Route / Driver", render: (r) => routeDriverLabel(r) },
    {
      key: "pod", header: "POD",
      render: (r) => (r.pof1 || r.pof2) ? (
        <button
          type="button"
          onClick={() => setPodDelivery(r)}
          className="flex items-center gap-1 text-xs text-primary hover:underline"
          data-testid={`delivery-pod-button-${r.id}`}
        >
          <ImageIcon size={13} strokeWidth={1.5} /> View
        </button>
      ) : <span className="text-xs text-text-muted">—</span>,
    },
    {
      key: "actions", header: "Actions",
      render: (r) => (
        <div className="flex items-center gap-1">
          <button
            type="button" title="Mark Pending" disabled={actingId === r.id || r.delivery_status === 1}
            onClick={() => setStatus(r, 1)}
            className="rounded-sm p-1 text-text-muted hover:bg-background hover:text-text-main disabled:opacity-30"
            data-testid={`delivery-mark-pending-${r.id}`}
          >
            <Clock size={16} strokeWidth={1.5} />
          </button>
          <button
            type="button" title="Mark Delivered" disabled={actingId === r.id || r.delivery_status === 2}
            onClick={() => setStatus(r, 2)}
            className="rounded-sm p-1 text-success hover:bg-success-bg disabled:opacity-30"
            data-testid={`delivery-mark-delivered-${r.id}`}
          >
            <CheckCircle2 size={16} strokeWidth={1.5} />
          </button>
          <button
            type="button" title="Mark Not Delivered" disabled={actingId === r.id || r.delivery_status === 3}
            onClick={() => setStatus(r, 3)}
            className="rounded-sm p-1 text-danger hover:bg-danger-bg disabled:opacity-30"
            data-testid={`delivery-mark-not-delivered-${r.id}`}
          >
            <XCircle size={16} strokeWidth={1.5} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="deliveries-page">
      <PageHeader
        title="Deliveries"
        description="Daily delivery schedule across all customers and routes."
        actions={
          <Button variant="accent" onClick={() => setGenerateOpen(true)} data-testid="generate-deliveries-button">
            <Sparkles size={16} strokeWidth={2} /> Generate Deliveries
          </Button>
        }
      />

      <PausedTiffinsPanel />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          type="date" className="w-40" value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="delivery-date-filter"
        />
        <div className="relative w-full max-w-xs">
          <Search size={15} strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            className="pl-9"
            placeholder="Search customer name or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="delivery-search-input"
          />
        </div>
        {date && (
          <Button variant="ghost" size="sm" onClick={() => setDate("")} data-testid="delivery-clear-date-button">
            Clear date
          </Button>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <MultiStatusFilter options={STATUS_OPTIONS} selected={statusFilters} onChange={setStatusFilters} testId="delivery-status-filter" />
        <SortControl
          fields={SORT_FIELDS} sortBy="date" onSortByChange={() => {}} sortDir={sortDir} onSortDirChange={setSortDir}
          testId="delivery-sort"
        />
      </div>

      <DataTable
        testId="deliveries-table"
        columns={columns}
        rows={items}
        loading={listQuery.isPending}
        error={error}
        onRetry={() => listQuery.refetch()}
        emptyTitle="No deliveries found"
        emptyDescription="Try a different date or filter, or generate deliveries for this date."
      />
      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testId="deliveries-pagination" />

      <GenerateDialog
        open={generateOpen} onOpenChange={setGenerateOpen}
        onDone={(generatedDate) => {
          setGenerateOpen(false);
          queryClient.invalidateQueries({ queryKey: ["deliveries-list"] });
          if (generatedDate !== date) setDate(generatedDate);
        }}
      />
      <PodViewerDialog open={!!podDelivery} onOpenChange={(v) => !v && setPodDelivery(null)} delivery={podDelivery} />
    </div>
  );
}
